import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { AlertButtonColor, DialogService } from "@obg/mat.ui.core";
import { CommonState } from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	CORE_ROUTE,
	CurrencySelector,
	Jurisdiction,
	JurisdictionSelector,
	Message,
	MessageService,
	MessageType,
	MULTI_DEPOSIT_LIMIT_MESSAGE_CONTEXT,
	MultiDepositLimit,
	MultiDepositLimitAction,
	MultiDepositLimitResponse,
	MultiDepositLimitSelector,
	SessionLimitRequiredService,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { getPathName, getQueryParams } from "@obg/ngx.global";
import { RouteAction, RouteSelector, RouteService } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { join as lodashJoin, map as lodashMap, toPairs } from "lodash-es";
import { BehaviorSubject, combineLatest, Observable, timer } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";

import { MULTI_DEPOSIT_LIMIT_MESSAGE_DIALOG } from "../multi-deposit-limit-message/multi-deposit-limit-message.dialog";

import { MultiDepositFormValidatonService } from "./multi-deposit-form-validaton.service";
import { MultiDepositLimitService } from "./multi-deposit-limit-form.service";
import { DEPOSIT_LIMIT_CONFIRMATION_ALERT_ID, NOT_SET_TRANSLATION_KEY } from "./multi-deposit-limit.const";
import { JurisdictionAndDepositLimitsResponse } from "./multi-deposit-limit.model";

@Injectable()
export class MultiDepositLimitContainerService {
	currentLimits: MultiDepositLimitResponse | undefined;
	upcomingLimits: MultiDepositLimitResponse | undefined;
	jurisdiction: Jurisdiction | undefined;

	readonly jurisdictionAndDepositLimits$: Observable<JurisdictionAndDepositLimitsResponse>;
	readonly isSessionLimitMissing$: Observable<boolean>;
	readonly currencySymbol$: Observable<string | undefined>;
	readonly redirectToDeposit$: Observable<boolean>;
	readonly depositLimitUpdate$: Observable<Message[]>;
	readonly placeholder = NOT_SET_TRANSLATION_KEY;

	get multiDepositForm() {
		return this.multiDepositLimitFormService.multiDepositLimitFormGroup;
	}

	get suggestions() {
		return this.multiDepositLimitFormService.suggestions;
	}

	get forceInvalidForm() {
		return this.multiDepositFormValidatonService.forceInvalidForm;
	}

	get validationKey() {
		return this.multiDepositFormValidatonService.validationKey;
	}

	get isSetDepositLoading$() {
		return this.setDepositIsLoading$.asObservable();
	}

	get isInputFieldsVisible$() {
		return this.showInputFields$.asObservable();
	}

	private returnUrl: string | undefined;
	private navigateToReturnUrl = false;
	private setDepositIsLoading$ = new BehaviorSubject<boolean>(false);
	private showInputFields$ = new BehaviorSubject<boolean>(false);

	constructor(
		private store: Store<CommonState>,
		private currencySelector: CurrencySelector,
		private multiDepositLimitSelector: MultiDepositLimitSelector,
		private translatableAlertService: AbstractTranslatableAlertService,
		private routeService: RouteService,
		private sessionLimitRequireService: SessionLimitRequiredService,
		private multiDepositLimitFormService: MultiDepositLimitService,
		private messageService: MessageService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private multiDepositFormValidatonService: MultiDepositFormValidatonService,
		private dialogService: DialogService,
		private multiDepositLimitAction: MultiDepositLimitAction
	) {
		this.jurisdictionAndDepositLimits$ = this.initJurisdictionAndDepositLimits$();
		this.currencySymbol$ = this.initCurrencySymbol$();
		this.redirectToDeposit$ = this.initRedirectToDeposit$();
		this.depositLimitUpdate$ = this.initSetDepositIsLoading$();
		this.isSessionLimitMissing$ = this.sessionLimitRequireService.isSessionLimitUnset$();
	}

	init(navigateToReturnUrl: boolean): void {
		this.navigateToReturnUrl = navigateToReturnUrl;
	}

	initFormService(): void {
		this.multiDepositLimitFormService.init(this.currentLimits, this.upcomingLimits, this.jurisdiction);
	}

	resetForm(): void {
		this.multiDepositLimitFormService.reset();
	}

	updateDepositLimits(): void {
		this.multiDepositLimitFormService.updateValue();
	}

	getWeeklyDepositValue(): number {
		return Number(this.multiDepositLimitFormService.multiDepositLimitFormGroup.value?.weeklyDepositLimit);
	}

	toggleInputsVisibility(isInputsVisible: boolean): void {
		this.showInputFields$.next(isInputsVisible);
	}

	submitForm(redirectToDeposit: boolean | undefined, depositLimitExceedMessage: string): void {
		if (redirectToDeposit) {
			this.updateDepositLimits();
			this.setDepositIsLoading$.next(true);
			return;
		}
		// hack: for iPhone, setTimeout waits until keyboard closes and then opens dialog
		// todo: refactor without using setTimeout https://jirasson.betsson.local/browse/OBGB-9394
		timer(100)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe(() => {
				if (this.jurisdiction === Jurisdiction.COGA) {
					this.dialogService
						.openComponent(MULTI_DEPOSIT_LIMIT_MESSAGE_DIALOG, {
							width: "490px",
							data: {
								newDepositLimits: this.newDepositLimits,
								displayInfoText: true,
								hideFooter: true,
								hideClose: true,
							},
						})
						.afterClosed()
						// eslint-disable-next-line rxjs/no-ignored-subscription
						.subscribe((confirmed) => {
							if (confirmed) {
								this.updateDepositLimits();
								this.setDepositIsLoading$.next(true);
							}
						});
					return;
				}

				this.translatableAlertService.open(
					{
						id: DEPOSIT_LIMIT_CONFIRMATION_ALERT_ID,
						title: "deposit-limit.confirmation-title",
						message: depositLimitExceedMessage,
						buttons: [
							{
								color: AlertButtonColor.Primary,
								text: "general.close",
							},
							{
								text: "general.ok",
								handler: () => {
									this.updateDepositLimits();
									this.setDepositIsLoading$.next(true);
								},
							},
						],
						hideClose: true,
						dialogConfig: {
							disableClose: true,
						},
						mustBeInteracted: true,
					},
					[TranslationGroup.General]
				);
			});
	}

	get newDepositLimits(): MultiDepositLimit {
		return {
			dailyDepositLimit: Number(
				this.multiDepositLimitFormService.multiDepositLimitFormGroup.value?.dailyDepositLimit
			),
			weeklyDepositLimit: Number(
				this.multiDepositLimitFormService.multiDepositLimitFormGroup.value?.weeklyDepositLimit
			),
			monthlyDepositLimit: Number(
				this.multiDepositLimitFormService.multiDepositLimitFormGroup.value?.monthlyDepositLimit
			),
		};
	}

	navigateToResponsibleGaming(): void {
		this.store.dispatch(RouteAction.navigate(CORE_ROUTE.responsibleGaming.depositLimit));
	}

	private initJurisdictionAndDepositLimits$() {
		return combineLatest([
			this.store.pipe(select(JurisdictionSelector.get, ignoreNil())),
			this.store.pipe(select(this.multiDepositLimitSelector.get())),
		]).pipe(
			map(([jurisdiction, depositLimits]) => {
				this.jurisdiction = jurisdiction;
				this.currentLimits = depositLimits?.depositLimit;
				this.upcomingLimits = depositLimits?.upcomingDepositLimit;
				this.initFormService();
				return {
					jurisdiction,
					currentWeeklyLimit: this.currentLimits?.weeklyDepositLimit,
					hasCurrentLimits: !!depositLimits,
				};
			})
		);
	}

	private initCurrencySymbol$(): Observable<string | undefined> {
		return this.store.pipe(select(this.currencySelector.getByCurrentCustomer())).pipe(
			filter((currency) => !!currency),
			map((currency) => currency?.symbol)
		);
	}

	private initRedirectToDeposit$(): Observable<boolean> {
		return this.store.select<{ returnUrl: string | undefined } | undefined>(RouteSelector.getQueryParams).pipe(
			tap((params) => (this.returnUrl = params?.returnUrl)),
			map(
				(params) =>
					!!params?.returnUrl &&
					this.routeService
						.generateUrl({
							routeName: CORE_ROUTE.payment.deposit,
						})
						.includes(params.returnUrl)
			)
		);
	}

	private initSetDepositIsLoading$(): Observable<Message[]> {
		return this.messageService
			.consume$({
				context: MULTI_DEPOSIT_LIMIT_MESSAGE_CONTEXT.update,
			})
			.pipe(
				tap((message) => {
					this.setDepositIsLoading$.next(false);
					this.resetForm();
					if (message[0].type === MessageType.Success) {
						this.updateSuccess();
						return;
					}

					this.updateFail();
				})
			);
	}

	private updateFail(): void {
		this.snackBarService
			.openSnackBar$(
				{
					message: "general.e",
					duration: 4000,
					action: "general.close",
					type: MessageType.Error,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
	}

	private updateSuccess(): void {
		this.showInputFields$.next(false);
		// hack: if return url is present then deposit-limits are used before deposit as part of jurisdiction requirments.
		// asuming that deposit is popup, we need to navigate back to referrer, not to stuck on deposit-limit page forever.
		// looks like it's the easiest way to implement it when deposit-limits is a stand alone page
		if (this.returnUrl) {
			this.store.dispatch(this.multiDepositLimitAction.resetDependencies());
			if (this.navigateToReturnUrl) {
				this.store.dispatch(RouteAction.navigateByUrl(this.returnUrl, { replaceUrl: true }));
				return;
			}
			const returnUrlQueryParams = getQueryParams(this.returnUrl);
			this.store
				.select(RouteSelector.getReferer)
				.pipe(
					take(1),
					tap((ref) => {
						// easy way to navigate somewhere and open dialog at the same time - is navigation + action=dialog_route
						const queryParams = { action: CORE_ROUTE.payment.deposit, ...returnUrlQueryParams };
						if (ref) {
							const params = lodashJoin(
								lodashMap(
									toPairs({ ...queryParams, ...getQueryParams(ref.externalUrl) }),
									(p) => `${p[0]}=${p[1]}`
								),
								"&"
							);
							const refExternalUrl = `${getPathName(ref.externalUrl)}?${params}`;
							this.store.dispatch(RouteAction.navigateByUrl(refExternalUrl, { replaceUrl: true }));
							return;
						}

						this.store.dispatch(
							RouteAction.navigate(CORE_ROUTE.home, null, { queryParams, replaceUrl: true })
						);
					})
				)
				.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
		}

		this.snackBarService
			.openSnackBar$(
				{
					message: "multi-deposit-limit.success-message",
					duration: 4000,
					action: "general.close",
					type: MessageType.Success,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
	}
}
